import React from "react";
import { useNavigate } from "react-router-dom";
import { useGscConnection } from "../hooks/useGscConnection";
import GSCPropertySelection from "./GSCPropertySelection";

const CONNECT_TAB_PATH = "/social-posts?tab=connect";

/** Page chrome shared by the loading, error and picker states. */
const CallbackShell: React.FC<{ onBack: () => void; children: React.ReactNode }> = ({
  onBack,
  children,
}) => (
  <div className="flex flex-col min-h-screen p-4">
    <div className="w-full flex justify-start pl-4 mb-2">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        Back
      </button>
    </div>
    <div className="flex flex-col items-center w-full flex-1">{children}</div>
  </div>
);

const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
    {children}
  </div>
);

const GSCCallback: React.FC = () => {
  const navigate = useNavigate();
  const { loading, error, properties, email, customerId } = useGscConnection();

  const goToConnect = () => navigate(CONNECT_TAB_PATH);

  if (loading) {
    return (
      <CallbackShell onBack={goToConnect}>
        <div className="flex flex-col items-center justify-center flex-1">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-brand-primary rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Connecting to Google Search Console...</p>
          </div>
        </div>
      </CallbackShell>
    );
  }

  if (error || !customerId) {
    return (
      <CallbackShell onBack={goToConnect}>
        <Panel>
          <p className="text-red-600 mb-4">
            {error || "No business profile selected. Please choose one and connect again."}
          </p>
          <button
            type="button"
            onClick={goToConnect}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover"
          >
            Back to Connect
          </button>
        </Panel>
      </CallbackShell>
    );
  }

  // A Google account with no verified property is a normal outcome, not a failure —
  // the fix is in Search Console, so say that rather than showing an empty grid.
  if (properties.length === 0) {
    return (
      <CallbackShell onBack={goToConnect}>
        <Panel>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            No Search Console properties found
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            {email ? <span className="font-medium">{email}</span> : "This Google account"}
            {" "}has no verified Search Console properties. Verify a property in Google
            Search Console, then connect again.
          </p>
          <button
            type="button"
            onClick={goToConnect}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover"
          >
            Back to Connect
          </button>
        </Panel>
      </CallbackShell>
    );
  }

  return (
    <CallbackShell onBack={goToConnect}>
      <Panel>
        <GSCPropertySelection
          properties={properties}
          customerId={customerId}
          email={email}
          onClose={goToConnect}
        />
      </Panel>
    </CallbackShell>
  );
};

export default GSCCallback;
